import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AdminCrossCurricularContentTable, {
  createEmptyCrossCurricularRow,
  type CrossCurricularTableRow,
} from '../components/admin/AdminCrossCurricularContentTable';
import ProgramToolbarUndoRedoButtons from '../components/program/ProgramToolbarUndoRedoButtons';
import {
  createAdminCrossCurricularTopic,
  deleteAdminCrossCurricularTopic,
  listAdminCrossCurricularCatalog,
  reorderAdminCrossCurricularTopics,
  replaceAdminCrossCurricularContentsForTopic,
  updateAdminCrossCurricularTopic,
} from '../services/adminCrossCurricularService';
import { usePlatformContentEditorAccess } from '../hooks/usePlatformContentEditorAccess';
import type {
  AdminCrossCurricularContent,
  AdminCrossCurricularTopic,
} from '../types/adminCrossCurricular';

function contentsToTableRows(
  topic: AdminCrossCurricularTopic,
  contents: AdminCrossCurricularContent[]
): CrossCurricularTableRow[] {
  const rows = contents
    .filter((c) => c.topic_id === topic.id)
    .slice()
    .sort((a, b) => a.sort_order - b.sort_order || a.created_at.localeCompare(b.created_at))
    .map((c, idx) => ({
      _localId: c.id,
      id: c.id,
      topic_name: topic.name,
      body: c.body,
      sort_order: idx,
    }));
  return rows.length ? rows : [createEmptyCrossCurricularRow(topic.name, 0)];
}

const AdminCrossCurricularPage: React.FC = () => {
  const { ready: accessReady, allowed: canAccess } = usePlatformContentEditorAccess();
  const [topics, setTopics] = useState<AdminCrossCurricularTopic[]>([]);
  const [contents, setContents] = useState<AdminCrossCurricularContent[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [topicOrderDirty, setTopicOrderDirty] = useState(false);

  const [selectedTopicId, setSelectedTopicId] = useState<string | null>(null);
  const [tableActions, setTableActions] = useState<{
    undo: () => void;
    redo: () => void;
    save: () => Promise<void>;
  } | null>(null);
  const [tableActionState, setTableActionState] = useState({
    canUndo: false,
    canRedo: false,
    canSave: false,
  });

  const [topicModal, setTopicModal] = useState<{
    mode: 'create' | 'rename';
    topicId: string | null;
    name: string;
  } | null>(null);

  const [deleteTopicConfirm, setDeleteTopicConfirm] = useState<{
    id: string;
    name: string;
    contentCount: number;
  } | null>(null);

  const topicItemRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const focusTableRef = useRef<(() => void) | null>(null);

  const loadCatalog = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const catalog = await listAdminCrossCurricularCatalog();
      setTopics(catalog.topics);
      setContents(catalog.contents);
      setTopicOrderDirty(false);
      setSelectedTopicId((prev) => {
        if (prev && catalog.topics.some((t) => t.id === prev)) return prev;
        return catalog.topics[0]?.id ?? null;
      });
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : '목록을 불러오지 못했습니다.');
      setTopics([]);
      setContents([]);
      setTopicOrderDirty(false);
      setSelectedTopicId(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!canAccess) return;
    void loadCatalog();
  }, [canAccess, loadCatalog]);

  const selectedTopic = useMemo(
    () => topics.find((t) => t.id === selectedTopicId) ?? null,
    [topics, selectedTopicId]
  );

  const contentCountByTopic = useMemo(() => {
    const map = new Map<string, number>();
    for (const c of contents) {
      map.set(c.topic_id, (map.get(c.topic_id) ?? 0) + 1);
    }
    return map;
  }, [contents]);

  const sourceRows = useMemo(() => {
    if (!selectedTopic) return [] as CrossCurricularTableRow[];
    return contentsToTableRows(selectedTopic, contents);
  }, [selectedTopic?.id, selectedTopic?.name, contents]);

  const dirty = tableActionState.canSave;
  const canSavePage = dirty || topicOrderDirty;
  const rowsReady = Boolean(selectedTopic) && !loading;
  const selectedTopicIndex = useMemo(
    () => (selectedTopicId ? topics.findIndex((t) => t.id === selectedTopicId) : -1),
    [topics, selectedTopicId]
  );
  const canMoveTopicUp = selectedTopicIndex > 0;
  const canMoveTopicDown =
    selectedTopicIndex >= 0 && selectedTopicIndex < topics.length - 1;

  useEffect(() => {
    setTableActionState({ canUndo: false, canRedo: false, canSave: false });
  }, [selectedTopicId, loading]);

  const openCreateTopic = () => {
    setActionError(null);
    setTopicModal({ mode: 'create', topicId: null, name: '' });
  };

  const moveSelectedTopic = (delta: -1 | 1) => {
    if (!selectedTopicId || busy || saving) return;
    const idx = topics.findIndex((t) => t.id === selectedTopicId);
    const nextIdx = idx + delta;
    if (idx < 0 || nextIdx < 0 || nextIdx >= topics.length) return;
    const next = [...topics];
    const [item] = next.splice(idx, 1);
    next.splice(nextIdx, 0, item);
    setTopics(next.map((t, i) => ({ ...t, sort_order: i })));
    setTopicOrderDirty(true);
  };

  const openRenameTopic = (topic: AdminCrossCurricularTopic) => {
    setActionError(null);
    setTopicModal({ mode: 'rename', topicId: topic.id, name: topic.name });
  };

  const handleSaveTopic = async () => {
    if (!topicModal || busy) return;
    setBusy(true);
    setActionError(null);
    try {
      if (topicModal.mode === 'create') {
        const created = await createAdminCrossCurricularTopic(topicModal.name.trim() || '새 주제어');
        setTopics((prev) => [...prev, created]);
        setSelectedTopicId(created.id);
      } else if (topicModal.topicId) {
        const updated = await updateAdminCrossCurricularTopic(topicModal.topicId, {
          name: topicModal.name,
        });
        setTopics((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
      }
      setTopicModal(null);
    } catch (e) {
      setActionError(e instanceof Error ? e.message : '주제어 저장에 실패했습니다.');
    } finally {
      setBusy(false);
    }
  };

  const handleConfirmDeleteTopic = async () => {
    if (!deleteTopicConfirm || busy) return;
    setBusy(true);
    setActionError(null);
    try {
      await deleteAdminCrossCurricularTopic(deleteTopicConfirm.id);
      setTopics((prev) => prev.filter((t) => t.id !== deleteTopicConfirm.id));
      setContents((prev) => prev.filter((c) => c.topic_id !== deleteTopicConfirm.id));
      setSelectedTopicId((prev) => (prev === deleteTopicConfirm.id ? null : prev));
      setDeleteTopicConfirm(null);
    } catch (e) {
      setActionError(e instanceof Error ? e.message : '주제어 삭제에 실패했습니다.');
    } finally {
      setBusy(false);
    }
  };

  const handleSaveContents = async (draftRows: CrossCurricularTableRow[]) => {
    if (!selectedTopic || saving) return;
    setSaving(true);
    setActionError(null);
    try {
      if (topicOrderDirty) {
        await reorderAdminCrossCurricularTopics(topics.map((t) => t.id));
        setTopicOrderDirty(false);
      }
      const draftTopicName =
        draftRows.map((r) => r.topic_name.trim()).find(Boolean) || selectedTopic.name;
      let topic = selectedTopic;
      if (draftTopicName !== selectedTopic.name) {
        topic = await updateAdminCrossCurricularTopic(selectedTopic.id, { name: draftTopicName });
        setTopics((prev) => prev.map((t) => (t.id === topic.id ? topic : t)));
      }
      const saved = await replaceAdminCrossCurricularContentsForTopic(
        topic.id,
        draftRows.map((r) => ({ id: r.id, body: r.body }))
      );
      setContents((prev) => [...prev.filter((c) => c.topic_id !== topic.id), ...saved]);
    } catch (e) {
      setActionError(e instanceof Error ? e.message : '학습내용 저장에 실패했습니다.');
      throw e;
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAll = async () => {
    if (saving || busy) return;
    if (!canSavePage) return;
    if (dirty) {
      await tableActions?.save();
      return;
    }
    if (!topicOrderDirty) return;
    setSaving(true);
    setActionError(null);
    try {
      await reorderAdminCrossCurricularTopics(topics.map((t) => t.id));
      setTopicOrderDirty(false);
    } catch (e) {
      setActionError(e instanceof Error ? e.message : '주제어 순서 저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const selectTopic = (topicId: string) => {
    if (dirty && selectedTopicId && selectedTopicId !== topicId) {
      const ok = window.confirm('저장하지 않은 학습내용 변경이 있습니다. 주제어를 바꿀까요?');
      if (!ok) return false;
    }
    setSelectedTopicId(topicId);
    return true;
  };

  const focusTopicItem = useCallback((topicId: string) => {
    window.requestAnimationFrame(() => {
      const el = topicItemRefs.current.get(topicId);
      el?.focus();
      el?.scrollIntoView({ block: 'nearest' });
    });
  }, []);

  const navigateTopicByArrow = (fromTopicId: string, delta: -1 | 1) => {
    const idx = topics.findIndex((t) => t.id === fromTopicId);
    const nextIdx = idx + delta;
    if (idx < 0 || nextIdx < 0 || nextIdx >= topics.length) return;
    const next = topics[nextIdx];
    if (!next) return;
    if (!selectTopic(next.id)) return;
    focusTopicItem(next.id);
  };

  const enterContentsTable = (fromTopicId: string) => {
    if (!selectTopic(fromTopicId)) return;
    window.requestAnimationFrame(() => {
      focusTableRef.current?.();
    });
  };

  if (!accessReady) {
    return (
      <section className="cp-admin-notices-page" aria-labelledby="cp-admin-cross-title">
        <h2 id="cp-admin-cross-title" className="cp-page-title">
          범교과 관리
        </h2>
        <p className="cp-dashboard-placeholder" role="status">
          확인 중…
        </p>
      </section>
    );
  }

  if (!canAccess) {
    return (
      <section className="cp-admin-notices-page" aria-labelledby="cp-admin-cross-title">
        <h2 id="cp-admin-cross-title" className="cp-page-title">
          범교과 관리
        </h2>
        <p className="cp-dashboard-placeholder" role="alert">
          이 메뉴는 관리자 또는 매니저만 사용할 수 있습니다.
        </p>
      </section>
    );
  }

  return (
    <section
      className="cp-admin-notices-page cp-admin-notices-page--edu cp-admin-cross-curricular-page"
      aria-labelledby="cp-admin-cross-title"
    >
      <div className="cp-page-title-row cp-admin-notices-page-title-row cp-evaluation-plan-page-title-row">
        <div className="cp-admin-legal-title-stack">
          <h2 id="cp-admin-cross-title">범교과 관리</h2>
          <p className="cp-admin-legal-subtitle">
            왼쪽에서 주제어를 선택하고, 오른쪽에 학습내용을 표로 입력합니다. 엑셀 복사·붙여넣기를 지원합니다.
          </p>
        </div>
        <div className="cp-annual-top-actions-save cp-admin-learning-top-right-actions cp-page-save-actions--chunky" role="group" aria-label="편집 저장 도구">
          <ProgramToolbarUndoRedoButtons
            canUndo={tableActionState.canUndo}
            canRedo={tableActionState.canRedo}
            onUndo={() => tableActions?.undo()}
            onRedo={() => tableActions?.redo()}
            disabled={!tableActions || !rowsReady}
          />
          <button
            type="button"
            className="cp-btn-save-all cp-admin-learning-top-save-btn"
            onClick={() => void handleSaveAll()}
            disabled={
              saving || busy || !canSavePage || (dirty && (!tableActions || !rowsReady))
            }
            title={canSavePage ? '저장하기' : '저장할 변경사항 없음'}
          >
            {saving || busy ? '저장 중…' : canSavePage ? '저장하기' : '변경사항 없음'}
          </button>
        </div>
      </div>

      {actionError ? (
        <p className="cp-dashboard-placeholder" role="alert">
          {actionError}
        </p>
      ) : null}

      {loading ? (
        <p className="cp-dashboard-placeholder" role="status">
          불러오는 중…
        </p>
      ) : loadError ? (
        <p className="cp-dashboard-placeholder" role="alert">
          {loadError}
        </p>
      ) : (
        <div className="cp-admin-cross-split">
          <aside
            className="cp-dashboard-block cp-admin-cross-topics-panel"
            aria-labelledby="cp-admin-cross-topics-head"
          >
            <h3 className="cp-dashboard-block__head cp-evaluation-plan-section-head" id="cp-admin-cross-topics-head">
              <span className="cp-evaluation-plan-section-head-title">주제어</span>
            </h3>
            <div className="cp-dashboard-block__body cp-admin-cross-topics-list">
              <div className="cp-program-progress-toolbar cp-admin-cross-topics-body-actions">
                <div className="cp-program-progress-toolbar-group" role="group" aria-label="주제어 순서·추가">
                  <button
                    type="button"
                    className="cp-btn-copy-paste cp-btn-swap-region"
                    onClick={() => moveSelectedTopic(-1)}
                    disabled={busy || saving || !canMoveTopicUp}
                    aria-label="위로 이동"
                    title="위로 이동"
                  >
                    <span className="cp-btn-label-desktop">위로 이동</span>
                    <span className="cp-btn-label-mobile-icon cp-btn-mobile-shift-icon" aria-hidden>
                      <svg viewBox="0 0 20 14" width="20" height="14" focusable="false">
                        <path d="M6 2 2.5 5.5l1.2 1.2L5 5.4V12h2V5.4l1.3 1.3 1.2-1.2L6 2z" fill="currentColor" />
                        <path
                          d="M14 12l3.5-3.5-1.2-1.2L15 8.6V2h-2v6.6l-1.3-1.3-1.2 1.2L14 12z"
                          fill="currentColor"
                          opacity="0.45"
                        />
                      </svg>
                    </span>
                  </button>
                  <button
                    type="button"
                    className="cp-btn-copy-paste cp-btn-swap-region"
                    onClick={() => moveSelectedTopic(1)}
                    disabled={busy || saving || !canMoveTopicDown}
                    aria-label="아래로 이동"
                    title="아래로 이동"
                  >
                    <span className="cp-btn-label-desktop">아래로 이동</span>
                    <span className="cp-btn-label-mobile-icon cp-btn-mobile-shift-icon" aria-hidden>
                      <svg viewBox="0 0 20 14" width="20" height="14" focusable="false">
                        <path d="M6 12l3.5-3.5-1.2-1.2L7 8.6V2H5v6.6L3.7 7.3 2.5 8.5 6 12z" fill="currentColor" />
                        <path
                          d="M14 2l-3.5 3.5 1.2 1.2L13 5.4V12h2V5.4l1.3 1.3 1.2-1.2L14 2z"
                          fill="currentColor"
                          opacity="0.45"
                        />
                      </svg>
                    </span>
                  </button>
                  <button
                    type="button"
                    className="cp-btn-copy-paste cp-btn-swap-region cp-btn-plusminus"
                    onClick={openCreateTopic}
                    disabled={busy}
                    aria-label="주제어 추가"
                    title="주제어 추가"
                  >
                    <span className="cp-btn-label-desktop">주제어 추가</span>
                    <span className="cp-btn-label-mobile-icon" aria-hidden>
                      +
                    </span>
                  </button>
                </div>
              </div>
              <div className="cp-admin-cross-topics-tablist" role="tablist" aria-label="주제어 목록">
                {topics.length === 0 ? (
                  <p className="cp-dashboard-placeholder">+ 로 주제어를 추가하세요.</p>
                ) : (
                  topics.map((topic) => {
                    const active = selectedTopicId === topic.id;
                    return (
                      <div
                        key={topic.id}
                        ref={(el) => {
                          if (el) topicItemRefs.current.set(topic.id, el);
                          else topicItemRefs.current.delete(topic.id);
                        }}
                        role="tab"
                        tabIndex={
                          active || (!selectedTopicId && topics[0]?.id === topic.id) ? 0 : -1
                        }
                        aria-selected={active}
                        className={`cp-admin-learning-template-item cp-admin-cross-topic-item${active ? ' is-active' : ''}`}
                        onClick={() => {
                          if (selectTopic(topic.id)) focusTopicItem(topic.id);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            if (selectTopic(topic.id)) focusTopicItem(topic.id);
                            return;
                          }
                          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                            e.preventDefault();
                            e.stopPropagation();
                            navigateTopicByArrow(topic.id, e.key === 'ArrowDown' ? 1 : -1);
                            return;
                          }
                          if (e.key === 'ArrowRight') {
                            e.preventDefault();
                            e.stopPropagation();
                            enterContentsTable(topic.id);
                          }
                        }}
                      >
                        <div className="cp-admin-learning-template-tab">
                          <span className="cp-admin-learning-template-tab-label">{topic.name}</span>
                          <span className="cp-admin-cross-topic-count">{contentCountByTopic.get(topic.id) ?? 0}</span>
                        </div>
                        <div className="cp-admin-learning-template-item-actions">
                          <button
                            type="button"
                            className="track-icon-button cp-admin-learning-template-icon-btn"
                            title="주제어 이름 수정"
                            aria-label={`${topic.name} 수정`}
                            tabIndex={-1}
                            onClick={(e) => {
                              e.stopPropagation();
                              openRenameTopic(topic);
                            }}
                            disabled={busy}
                          >
                            <img src="/edit.svg" alt="" className="track-icon" />
                          </button>
                          <button
                            type="button"
                            className="track-icon-button cp-admin-learning-template-icon-btn cp-admin-learning-template-icon-btn-delete"
                            title="주제어 삭제"
                            aria-label={`${topic.name} 삭제`}
                            tabIndex={-1}
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteTopicConfirm({
                                id: topic.id,
                                name: topic.name,
                                contentCount: contentCountByTopic.get(topic.id) ?? 0,
                              });
                            }}
                            disabled={busy}
                          >
                            <img src="/Trash.svg" alt="" className="track-icon" />
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </aside>

          <div
            className="cp-dashboard-block cp-admin-cross-contents-panel"
            aria-labelledby="cp-admin-cross-contents-head"
          >
            <h3 className="cp-dashboard-block__head cp-evaluation-plan-section-head" id="cp-admin-cross-contents-head">
              <span className="cp-evaluation-plan-section-head-title">
                학습내용
                {selectedTopic ? (
                  <span className="cp-admin-cross-contents-topic-name"> · {selectedTopic.name}</span>
                ) : null}
              </span>
            </h3>
            <div className="cp-dashboard-block__body cp-admin-cross-contents-body">
              {!selectedTopic ? (
                <p className="cp-dashboard-placeholder">왼쪽에서 주제어를 선택하세요.</p>
              ) : (
                <AdminCrossCurricularContentTable
                  key={selectedTopic.id}
                  topicName={selectedTopic.name}
                  rows={sourceRows}
                  saving={saving}
                  showInlineActionButtons={false}
                  onBindActions={setTableActions}
                  onActionStateChange={setTableActionState}
                  onBindFocusTable={(fn) => {
                    focusTableRef.current = fn;
                  }}
                  onLeaveToTopics={() => {
                    if (selectedTopicId) focusTopicItem(selectedTopicId);
                  }}
                  onSave={handleSaveContents}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {topicModal ? (
        <div className="cp-modal-backdrop" role="presentation" onClick={() => !busy && setTopicModal(null)}>
          <div
            className="cp-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cp-admin-cross-topic-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="cp-admin-cross-topic-modal-title">
              {topicModal.mode === 'create' ? '주제어 추가' : '주제어 이름 변경'}
            </h3>
            <div className="cp-modal-body">
              <label className="cp-evaluation-plan-field">
                <span>주제어</span>
                <input
                  type="text"
                  value={topicModal.name}
                  onChange={(e) => setTopicModal((prev) => (prev ? { ...prev, name: e.target.value } : prev))}
                  autoFocus
                  disabled={busy}
                  placeholder="예: 학교폭력"
                />
              </label>
            </div>
            <div className="cp-modal-footer">
              <button type="button" className="cp-btn-secondary" onClick={() => setTopicModal(null)} disabled={busy}>
                취소
              </button>
              <button type="button" className="cp-btn-primary" onClick={() => void handleSaveTopic()} disabled={busy}>
                {busy ? '저장 중…' : '저장'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {deleteTopicConfirm ? (
        <div className="cp-modal-backdrop" role="presentation" onClick={() => !busy && setDeleteTopicConfirm(null)}>
          <div
            className="cp-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cp-admin-cross-delete-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="cp-admin-cross-delete-title">주제어 삭제</h3>
            <div className="cp-modal-body">
              <p>
                「{deleteTopicConfirm.name}」을(를) 삭제할까요?
                {deleteTopicConfirm.contentCount > 0
                  ? ` 연결된 학습내용 ${deleteTopicConfirm.contentCount}건도 함께 삭제됩니다.`
                  : ''}
              </p>
            </div>
            <div className="cp-modal-footer">
              <button
                type="button"
                className="cp-btn-secondary"
                onClick={() => setDeleteTopicConfirm(null)}
                disabled={busy}
              >
                취소
              </button>
              <button type="button" className="cp-btn-primary" onClick={() => void handleConfirmDeleteTopic()} disabled={busy}>
                {busy ? '삭제 중…' : '삭제'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      <button
        type="button"
        className="cp-fab-save"
        onClick={() => void handleSaveAll()}
        disabled={
          saving || busy || !canSavePage || (dirty && (!tableActions || !rowsReady))
        }
        aria-label="저장하기"
        title={canSavePage ? '저장하기' : '저장할 변경사항 없음'}
      >
        <img
          src={`${process.env.PUBLIC_URL || ''}/Save.svg`}
          alt=""
          className="cp-fab-save-icon"
        />
      </button>
    </section>
  );
};

export default AdminCrossCurricularPage;
